/**
 * AddEventForm Component
 * 
 * Form for adding match events (goals, cards, substitutions, penalties).
 * Supports normal time, extra time, and penalty shootout periods.
 */
import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

/**
 * @param {Object} props
 * @param {Object[]} props.players - All players
 * @param {string[]} props.lineup - Array of player IDs in lineup
 * @param {Object[]} props.subs - Array of sub objects
 * @param {Function} props.onAdd - Handler to add event(s)
 * @param {Function} props.onClose - Handler to close the form
 * @param {Object} props.formData - Match form data (for cup/ET/pen state)
 */
export function AddEventForm({ players, lineup, subs, onAdd, onClose, formData }) {
  const [isOpponent, setIsOpponent] = useState(false);
  const [eventType, setEventType] = useState('goal');
  const [playerId, setPlayerId] = useState('');
  const [assistPlayerId, setAssistPlayerId] = useState('');
  const [subOutPlayerId, setSubOutPlayerId] = useState('');
  const [minute, setMinute] = useState('');
  const [period, setPeriod] = useState('normal');
  const [penResult, setPenResult] = useState('scored');
  const [penOrder, setPenOrder] = useState('');
  const [goalType, setGoalType] = useState('');
  const [isOwnGoal, setIsOwnGoal] = useState(false);
  const [ownGoalPlayerId, setOwnGoalPlayerId] = useState('');
  const [isPenaltyGoal, setIsPenaltyGoal] = useState(false);
  const [penaltyConcededById, setPenaltyConcededById] = useState('');

  const hasExtraTime = formData?.is_cup && formData?.our_score_et !== '' && formData?.our_score_et !== undefined;
  const hasPenalties = hasExtraTime && formData?.our_score_pens !== '' && formData?.our_score_pens !== undefined;

  const PERIODS = [
    { value: 'normal', label: '1st / 2nd Half' },
    ...(hasExtraTime ? [
      { value: 'et1', label: 'ET 1st Half' },
      { value: 'et2', label: 'ET 2nd Half' },
    ] : []),
    ...(hasPenalties ? [
      { value: 'shootout', label: 'Penalty Shootout' },
    ] : []),
  ];

  const isShootout = period === 'shootout';

  const GOAL_TYPES = ['goal', 'penalty_goal', 'own_goal'];
  const isGoalType = GOAL_TYPES.includes(eventType);
  const isSubstitution = eventType === 'substitution_in';

  const squadPlayerIds = [
    ...lineup.filter(Boolean),
    ...subs.filter(s => s && (typeof s === 'object' ? s.playerId : s)).map(s => typeof s === 'object' ? s.playerId : s)
  ];
  const squadPlayers = players.filter(p => squadPlayerIds.includes(p.id));
  const assistPlayers = squadPlayers.filter(p => p.id !== playerId);
  const subOutPlayers = squadPlayers.filter(p => p.id !== playerId);

  const getMinuteHint = () => {
    switch (period) {
      case 'et1': return '91-105';
      case 'et2': return '106-120';
      default: return '1-90';
    }
  };

  const handleSubmit = () => {
    if (isShootout) {
      if (!isOpponent && !playerId) {
        toast.error('Please select the penalty taker');
        return;
      }
      if (!penOrder) {
        toast.error('Please enter the penalty number');
        return;
      }

      const events = [{
        event_type: penResult === 'scored' ? 'pen_scored' : 'pen_missed',
        player_id: isOpponent ? undefined : playerId,
        is_opponent: isOpponent,
        minute: 121,
        period: 'shootout',
        pen_order: parseInt(penOrder)
      }];
      onAdd(events);
      setPlayerId('');
      setPenOrder(parseInt(penOrder) + 1 + '');
      setPenResult('scored');
      setIsOpponent(!isOpponent);
      return;
    }

    if (isOpponent) {
      if (!minute) {
        toast.error('Please enter the minute');
        return;
      }
    } else {
      if (!playerId || !minute) {
        toast.error('Please fill all required fields');
        return;
      }
      if (eventType === 'substitution_in' && !subOutPlayerId) {
        toast.error('Please select the player being substituted off');
        return;
      }
    }

    const events = [];
    const eventPeriod = period !== 'normal' ? period : undefined;

    if (isOpponent) {
      if (isOwnGoal && eventType === 'goal') {
        // Own goal by our player
        if (!ownGoalPlayerId) { toast.error('Select who scored the own goal'); return; }
        events.push({
          event_type: 'own_goal',
          player_id: ownGoalPlayerId,
          minute: parseInt(minute),
          ...(eventPeriod && { period: eventPeriod })
        });
      } else if (isPenaltyGoal && eventType === 'goal') {
        // Opponent penalty goal
        events.push({
          event_type: eventType,
          is_opponent: true,
          is_penalty: true,
          minute: parseInt(minute),
          ...(eventPeriod && { period: eventPeriod })
        });
        if (penaltyConcededById) {
          events.push({
            event_type: 'penalty_conceded',
            player_id: penaltyConcededById,
            minute: parseInt(minute),
            ...(eventPeriod && { period: eventPeriod })
          });
        }
      } else {
        events.push({
          event_type: eventType,
          is_opponent: true,
          minute: parseInt(minute),
          ...(eventPeriod && { period: eventPeriod })
        });
      }
    } else if (isGoalType) {
      const goalEvent = {
        event_type: eventType,
        player_id: playerId,
        minute: parseInt(minute),
        ...(eventPeriod && { period: eventPeriod }),
        ...(goalType && { goal_type: goalType })
      };
      events.push(goalEvent);
      if (assistPlayerId && assistPlayerId !== 'none' && eventType !== 'own_goal') {
        events.push({
          event_type: 'assist',
          player_id: assistPlayerId,
          minute: parseInt(minute),
          ...(eventPeriod && { period: eventPeriod })
        });
      }
    } else if (eventType === 'substitution_in') {
      events.push({
        event_type: 'substitution_out',
        player_id: subOutPlayerId,
        minute: parseInt(minute),
        ...(eventPeriod && { period: eventPeriod })
      });
      events.push({
        event_type: 'substitution_in',
        player_id: playerId,
        minute: parseInt(minute),
        ...(eventPeriod && { period: eventPeriod })
      });
    } else {
      events.push({
        event_type: eventType,
        player_id: playerId,
        minute: parseInt(minute),
        ...(eventPeriod && { period: eventPeriod })
      });
    }

    onAdd(events);
    setIsOpponent(false);
    setEventType('goal');
    setPlayerId('');
    setAssistPlayerId('');
    setSubOutPlayerId('');
    setMinute('');
    setGoalType('');
    setIsOwnGoal(false);
    setOwnGoalPlayerId('');
    setIsPenaltyGoal(false);
    setPenaltyConcededById('');
    onClose();
  };

  const isGoal = isGoalType;

  return (
    <div className="space-y-4 mt-4">
      {/* Period Selector */}
      {PERIODS.length > 1 && (
        <div className="space-y-2">
          <Label className="text-xs text-gray-500 uppercase tracking-wider">Match Period</Label>
          <div className="flex flex-wrap gap-1.5">
            {PERIODS.map(p => (
              <button
                key={p.value}
                type="button"
                onClick={() => {
                  setPeriod(p.value);
                  setPlayerId('');
                  setAssistPlayerId('');
                  setSubOutPlayerId('');
                  setMinute('');
                }}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  period === p.value
                    ? p.value === 'shootout' ? 'bg-red-600 text-white border-red-600' : 'bg-emerald-700 text-white border-emerald-700'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Penalty Shootout Form */}
      {isShootout ? (
        <>
          <div className="flex rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => { setIsOpponent(false); setPlayerId(''); }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                !isOpponent ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="our-team-btn"
            >
              Our Team
            </button>
            <button
              type="button"
              onClick={() => { setIsOpponent(true); setPlayerId(''); }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                isOpponent ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="opponent-btn"
            >
              Opponent
            </button>
          </div>

          <div className="space-y-2">
            <Label>Penalty #</Label>
            <Input
              type="number"
              min="1"
              max="20"
              value={penOrder}
              onChange={(e) => setPenOrder(e.target.value)}
              placeholder="e.g. 1"
              className="rounded-xl"
              data-testid="pen-order-input"
            />
          </div>

          {!isOpponent && (
            <div className="space-y-2">
              <Label>Penalty Taken By</Label>
              <Select value={playerId} onValueChange={setPlayerId}>
                <SelectTrigger className="rounded-xl" data-testid="pen-player-select">
                  <SelectValue placeholder="Select player" />
                </SelectTrigger>
                <SelectContent>
                  {squadPlayers.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      #{player.jersey_number || '?'} {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Result</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPenResult('scored')}
                className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                  penResult === 'scored'
                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
                data-testid="pen-result-scored"
              >
                Scored
              </button>
              <button
                type="button"
                onClick={() => setPenResult('missed')}
                className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                  penResult === 'missed'
                    ? 'bg-red-50 border-red-500 text-red-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
                data-testid="pen-result-missed"
              >
                Missed
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} className="rounded-xl">
              Done
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="rounded-xl bg-red-600 hover:bg-red-700"
              data-testid="add-pen-btn"
            >
              Add Penalty
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Team Toggle */}
          <div className="flex rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => {
                setIsOpponent(false);
                setPlayerId('');
                setAssistPlayerId('');
                setSubOutPlayerId('');
              }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                !isOpponent 
                  ? 'bg-white text-emerald-700 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="our-team-btn"
            >
              Our Team
            </button>
            <button
              type="button"
              onClick={() => {
                setIsOpponent(true);
                setPlayerId('');
                setAssistPlayerId('');
                setSubOutPlayerId('');
                if (eventType === 'substitution_in') {
                  setEventType('goal');
                }
              }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                isOpponent 
                  ? 'bg-white text-red-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="opponent-btn"
            >
              Opponent
            </button>
          </div>

          {/* Event Type */}
          <div className="space-y-2">
            <Label>Event Type</Label>
            <Select value={eventType} onValueChange={(v) => {
              setEventType(v);
              setPlayerId('');
              setAssistPlayerId('');
              setSubOutPlayerId('');
              setGoalType('');
              setIsOwnGoal(false);
              setOwnGoalPlayerId('');
              setIsPenaltyGoal(false);
              setPenaltyConcededById('');
            }}>
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Goals</div>
                <SelectItem value="goal">
                  <span className="flex items-center gap-2"><span>⚽</span><span>Goal</span></span>
                </SelectItem>
                <SelectItem value="penalty_goal">
                  <span className="flex items-center gap-2"><span>⚽</span><span>Penalty</span></span>
                </SelectItem>
                {!isOpponent && (
                  <SelectItem value="own_goal">
                    <span className="flex items-center gap-2"><span>⚽</span><span>Own Goal</span></span>
                  </SelectItem>
                )}
                <SelectItem value="missed_penalty">
                  <span className="flex items-center gap-2"><span>❌</span><span>Missed Penalty</span></span>
                </SelectItem>
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1">Cards</div>
                <SelectItem value="yellow_card">
                  <span className="flex items-center gap-2"><span>🟨</span><span>Yellow Card</span></span>
                </SelectItem>
                <SelectItem value="red_card">
                  <span className="flex items-center gap-2"><span>🟥</span><span>Red Card</span></span>
                </SelectItem>
                {!isOpponent && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1">Substitution</div>
                    <SelectItem value="substitution_in">
                      <span className="flex items-center gap-2"><span>🔄</span><span>Substitution</span></span>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Player Selection - Only for Our Team */}
          {!isOpponent && (
            <div className="space-y-2">
              <Label>{isGoal ? (eventType === 'own_goal' ? 'Own Goal By' : 'Goal Scored By') : isSubstitution ? 'Player Coming On' : eventType === 'missed_penalty' ? 'Penalty Taken By' : 'Player'}</Label>
              <Select value={playerId} onValueChange={setPlayerId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select player" />
                </SelectTrigger>
                <SelectContent>
                  {squadPlayers.length > 0 ? (
                    squadPlayers.map(player => (
                      <SelectItem key={player.id} value={player.id}>
                        #{player.jersey_number || '?'} {player.name}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-4 text-sm text-gray-500 text-center">
                      No players in squad. Add players to lineup first.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Assist selector for goals */}
          {!isOpponent && (eventType === 'goal' || eventType === 'penalty_goal') && playerId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Assist By</span>
                <span className="text-xs text-gray-400 font-normal">(optional)</span>
              </Label>
              <Select value={assistPlayerId} onValueChange={setAssistPlayerId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select assist player (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assist</SelectItem>
                  {assistPlayers.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      #{player.jersey_number || '?'} {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Goal Type for our team goals */}
          {!isOpponent && (eventType === 'goal' || eventType === 'penalty_goal') && playerId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Goal Type</span>
                <span className="text-xs text-gray-400 font-normal">(optional)</span>
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: 'penalty', label: 'Penalty' },
                  { id: 'shot_inside', label: 'Shot (Inside Box)' },
                  { id: 'shot_outside', label: 'Shot (Outside Box)' },
                  { id: 'header', label: 'Header' },
                  { id: 'own_goal', label: 'OG' },
                ].map(gt => (
                  <button
                    key={gt.id}
                    type="button"
                    onClick={() => setGoalType(goalType === gt.id ? '' : gt.id)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      goalType === gt.id ? 'bg-emerald-600 text-white ring-1 ring-emerald-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`goal-type-${gt.id}`}
                  >
                    {gt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* OG toggle for opponent goals */}
          {isOpponent && eventType === 'goal' && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setIsOwnGoal(prev => !prev); setOwnGoalPlayerId(''); setIsPenaltyGoal(false); setPenaltyConcededById(''); }}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                    isOwnGoal ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="event-own-goal-toggle"
                >
                  OG (Own Goal)
                </button>
                <button
                  type="button"
                  onClick={() => { setIsPenaltyGoal(prev => !prev); setPenaltyConcededById(''); setIsOwnGoal(false); setOwnGoalPlayerId(''); }}
                  className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                    isPenaltyGoal ? 'bg-red-600 text-white ring-1 ring-red-400' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="event-penalty-toggle"
                >
                  Penalty
                </button>
              </div>
            </div>
          )}

          {/* OG Player Selection */}
          {isOpponent && eventType === 'goal' && isOwnGoal && (
            <div className="space-y-2">
              <Label>Who scored the own goal?</Label>
              <Select value={ownGoalPlayerId} onValueChange={setOwnGoalPlayerId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select player" />
                </SelectTrigger>
                <SelectContent>
                  {squadPlayers.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      #{player.jersey_number || '?'} {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Penalty Conceded By selection */}
          {isOpponent && eventType === 'goal' && isPenaltyGoal && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Who conceded the penalty?</span>
                <span className="text-xs text-gray-400 font-normal">(optional)</span>
              </Label>
              <Select value={penaltyConcededById} onValueChange={setPenaltyConcededById}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select player (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {squadPlayers.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      #{player.jersey_number || '?'} {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Player out selector for substitutions */}
          {!isOpponent && isSubstitution && playerId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Player Going Off</span>
                <span className="text-xs text-red-500 font-normal">*</span>
              </Label>
              <Select value={subOutPlayerId} onValueChange={setSubOutPlayerId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select player going off" />
                </SelectTrigger>
                <SelectContent>
                  {subOutPlayers.map(player => (
                    <SelectItem key={player.id} value={player.id}>
                      #{player.jersey_number || '?'} {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Minute */}
          <div className="space-y-2">
            <Label>Minute {period !== 'normal' && <span className="text-xs text-gray-400 font-normal">({getMinuteHint()})</span>}</Label>
            <Input
              type="number"
              min="1"
              max="120"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
              placeholder={`e.g. ${period === 'et1' ? '95' : period === 'et2' ? '110' : '45'}`}
              className="rounded-xl"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              className={`rounded-xl ${isOpponent ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-700 hover:bg-emerald-800'}`}
            >
              Add Event
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
